/**
 * Shared constants for the Collateral Map feature.
 * Used by both client and server.
 * 
 * [COLLATERAL MAP] Added 2026-07-14
 */

export const COLLATERAL_MAP = {
  /** Maximum polygon vertices allowed (client + server enforced) */
  MAX_VERTICES: 500,

  /** Maximum GeoJSON payload size in bytes (200 KB) */
  MAX_PAYLOAD_BYTES: 200_000,

  /** Lao PDR geographic bounding box (WGS84) */
  LAO_BOUNDS: {
    minLon: 100.0,
    maxLon: 108.0,
    minLat: 13.9,
    maxLat: 22.5,
  },

  /** Default map center (Vientiane Capital) */
  DEFAULT_CENTER: { lat: 17.9757, lng: 102.6331 },

  /** Default zoom level for collateral map */
  DEFAULT_ZOOM: 16,

  /** Area unit conversion factors (1 unit = X m²) */
  AREA_UNITS: {
    /** 1 ເຮັກຕາ = 10,000 m² */
    HECTARE: 10_000,
    /** 1 ໄຮ່ = 1,600 m² */
    RAI: 1_600,
    /** 1 ງານ = 400 m² */
    NGAN: 400,
    /** 1 ຕາຣາງວາ = 4 m² */
    WA: 4,
  },

  /**
   * Loan statuses where collateral map CANNOT be edited.
   * Matches the locked statuses used across the CRS system.
   */
  LOCKED_STATUSES: [
    "branch_approved",
    "pending_disbursement",
    "forwarded_to_ho",
    "ho_received",
    "ho_reviewing",
    "ho_pending_documents",
    "ho_approved",
    "ho_returned",
    "branch_received_from_ho",
    "pending_ho_approval",
    "approved",
    "disbursed",
    "closed",
    "rejected",
    "cancelled",
    "disbursing",
    "ho_approved_final",
  ] as const,

  /** Roles that can draw/edit collateral map boundaries */
  EDIT_ROLES: ["credit_analyst", "credit_manager", "admin", "branch_manager"] as const,

  /** MapTiler tile URL templates */
  TILE_URLS: {
    streets: "https://api.maptiler.com/maps/streets/{z}/{x}/{y}.png",
    satellite: "https://api.maptiler.com/tiles/satellite/{z}/{x}/{y}.jpg",
    hybrid: "https://api.maptiler.com/maps/hybrid/{z}/{x}/{y}.jpg",
  },

  /** Polygon style */
  POLYGON_STYLE: {
    color: "#002975",      // STB blue stroke
    fillColor: "#002975",  // STB blue fill
    fillOpacity: 0.25,
    weight: 3,
    opacity: 0.9,
  },

  /** GPS marker color */
  GPS_MARKER_COLOR: "#002975",
} as const;

export type LockedStatus = (typeof COLLATERAL_MAP.LOCKED_STATUSES)[number];
export type EditRole = (typeof COLLATERAL_MAP.EDIT_ROLES)[number];
